/**
 * 약관 · 개인정보 처리방침 · AI 안내 보기 (DEV-07r · D97). 로그인 없이 열림(앱 스토어 등록 · 가입 전 보기).
 *
 *   /legal        세 문서 목록
 *   /legal/:doc   문서 하나(terms · privacy · ai): 머리에 판 · 시행일, 본문은 마크다운(표는 가로 스크롤)
 * 원본은 content/legal(PM 파일), 서버 GET /api/legal/:doc 이 그대로 내보냄. 라이트 테마, 본문 서체.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { TopBar } from '../../components/TopBar.jsx';
import { Markdown } from '../../components/Markdown.jsx';
import { fetchLegalDoc } from '../../api/legal.js';
import { errorMessageOf } from '../../api/client.js';
import { useGoBack } from '../../hooks/useGoBack.js';
import { useSession } from '../../stores/session.js';

export const LEGAL_LINKS = [
    { key: 'terms', title: '이용약관' },
    { key: 'privacy', title: '개인정보 처리방침' },
    { key: 'ai', title: 'AI 대화 이용 안내' },
];

function useBackTarget() {
    const status = useSession((state) => state.status);
    return useGoBack(status === 'member' ? '/me' : '/start');
}

/** /legal — 세 문서 목록(나 탭 「약관 · AI 고지」에서) */
export function LegalIndexPage() {
    const navigate = useNavigate();
    const goBack = useBackTarget();
    return (
        <div className="flex h-dvh flex-col bg-bg">
            <TopBar title="약관 · AI 고지" onBack={goBack} />
            <main className="flex flex-col gap-2 overflow-y-auto px-4 pt-5 pb-6">
                {LEGAL_LINKS.map((item) => (
                    <button
                        key={item.key}
                        type="button"
                        onClick={() => navigate(`/legal/${item.key}`)}
                        className="flex h-14 items-center gap-3 rounded-card border border-hairline bg-surface px-4 text-left"
                    >
                        <span className="flex-1 text-[15px] font-semibold">{item.title}</span>
                        <ChevronRight size={18} strokeWidth={1.6} aria-hidden="true" className="text-text-secondary" />
                    </button>
                ))}
                <p className="m-0 mt-3 text-center text-caption leading-5 text-text-secondary">
                    인생상회의 멤버들은 AI가 연기하는 가상 인물이에요. 운세는 재미로 봐 주세요.
                </p>
            </main>
        </div>
    );
}

/** /legal/:doc */
export function LegalPage() {
    const { doc } = useParams();
    const goBack = useBackTarget();
    const [state, setState] = useState({ status: 'loading', doc: null, error: null });

    useEffect(() => {
        const controller = new AbortController();
        setState({ status: 'loading', doc: null, error: null });
        fetchLegalDoc(doc, { signal: controller.signal })
            .then((response) => setState({ status: 'ready', doc: response, error: null }))
            .catch((error) => {
                if (!controller.signal.aborted) {
                    setState({ status: 'error', doc: null, error: errorMessageOf(error, '문서를 불러오지 못했어요.') });
                }
            });
        return () => controller.abort();
    }, [doc]);

    const title = state.doc?.title ?? LEGAL_LINKS.find((item) => item.key === doc)?.title ?? '약관';
    return (
        <div className="flex h-dvh flex-col bg-bg">
            <TopBar title={title} onBack={goBack} />
            <main className="min-h-0 flex-1 overflow-y-auto px-4 pt-4 pb-10">
                {state.status === 'loading' ? (
                    <p className="m-0 text-[14px] text-text-secondary" aria-busy="true">
                        불러오는 중…
                    </p>
                ) : null}
                {state.status === 'error' ? (
                    <p role="alert" className="m-0 text-[14px] text-error">
                        {state.error}
                    </p>
                ) : null}
                {state.doc ? (
                    <article>
                        <p className="m-0 mb-3 text-[12px] leading-[18px] text-text-secondary">
                            {[state.doc.version, state.doc.effective ? `시행일 ${state.doc.effective}` : null]
                                .filter(Boolean)
                                .join(' · ')}
                            {state.doc.draft ? ' · 초안' : ''}
                        </p>
                        <Markdown markdown={state.doc.markdown} />
                    </article>
                ) : null}
            </main>
        </div>
    );
}
